import React, { ReactNode, useState } from "react";
import { MessageType } from "../../models/element";
import {
    Card,
    CardHeader,
    Collapse,
    IconButton,
    makeStyles,
    Paper,
    Slide,
    SlideProps,
    Snackbar,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import classNames from "classnames";
import { uniq } from "../../utils/ObjectUtils";
import PuiTypography from "../elements/PuiTypography";

interface MessageCardProps {
    // 表示フラグ
    open: boolean;
    // 種別
    type: MessageType;
    // タイトル
    title: ReactNode;
    // 詳細情報
    detail?: ReactNode[];
    // 自動非表示
    autoHide?: boolean;
    // 表示時間（autoHide=trueの場合のみ有効）
    duration?: number;
    // Closeイベント
    onClose?: () => void;
}

type TransitionProps = Omit<SlideProps, "direction">;
function transition(props: TransitionProps) {
    return <Slide {...props} direction="up" />;
}

const useStyles = makeStyles((theme) => ({
    root: { [theme.breakpoints.up("sm")]: { minWidth: "260px !important" } },
    card: { width: "100%" },
    header: {
        minHeight: "50px",
        maxHeight: "90vh",
        overflowY: "auto",
        position: "relative",
        alignItems: "normal",
    },
    success: { backgroundColor: "#43a047" },
    error: { backgroundColor: "#d32f2f" },
    info: { backgroundColor: "#2196f3" },
    warning: { backgroundColor: "#ff9800" },
    expand: {
        padding: "8px 8px",
        transform: "rotate(0deg)",
        transition: theme.transitions.create("transform", {
            duration: theme.transitions.duration.shortest,
        }),
        color: "white",
        marginLeft: "15px",
        marginTop: "-3px",
    },
    expandOpen: { transform: "rotate(180deg)" },
    close: {
        padding: "8px 8px",
        color: "white",
        marginTop: "-3px",
    },
    collapse: { padding: "15px 20px" },
}));

function PuiMessage({
    open,
    type,
    title,
    detail = [],
    autoHide = true,
    duration = 6000,
    onClose,
}: MessageCardProps) {
    const classes = useStyles();
    const [expanded, setExpanded] = useState(false);

    const handleExpandClick = () => {
        setExpanded(!expanded);
    };

    const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
        if (reason === "clickaway") {
            return;
        }

        setExpanded(false);

        if (onClose) {
            onClose();
        }
    };

    return (
        <Snackbar
            open={open}
            TransitionComponent={transition}
            autoHideDuration={autoHide ? duration : null}
            onClose={handleClose}
            className={classes.root}
        >
            <Card
                className={classNames(classes.card, {
                    [classes.success]: type === "success",
                    [classes.error]: type === "error",
                    [classes.info]: type === "info",
                    [classes.warning]: type === "warning",
                })}
            >
                <CardHeader
                    action={
                        <>
                            {detail.length > 0 && (
                                <IconButton
                                    className={classNames(classes.expand, {
                                        [classes.expandOpen]: expanded,
                                    })}
                                    onClick={handleExpandClick}
                                >
                                    <ExpandMoreIcon />
                                </IconButton>
                            )}
                            <IconButton className={classes.close} onClick={handleClose}>
                                <CloseIcon />
                            </IconButton>
                        </>
                    }
                    disableTypography={true}
                    title={
                        <PuiTypography bold={true} color={"white"}>
                            {title}
                        </PuiTypography>
                    }
                    className={classes.header}
                />
                {detail.length > 0 && (
                    <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Paper className={classes.collapse}>
                            {uniq(detail).map((content, index) => (
                                <div
                                    key={index}
                                    style={{
                                        marginBottom: index !== detail.length - 1 ? "15px" : "0px",
                                    }}
                                >
                                    {content}
                                </div>
                            ))}
                        </Paper>
                    </Collapse>
                )}
            </Card>
        </Snackbar>
    );
}

export default PuiMessage;
